import axios from "axios";
import type { AxiosRequestConfig } from "axios";

import { getAuthToken } from "@/core/auth";

import { SUCCESS_STATUS_CODES } from "./api.config";
import type {
  DeletePaths,
  DeleteRequest,
  DeleteResult,
  GetPaths,
  GetRequest,
  GetResult,
  PatchPaths,
  PatchRequest,
  PatchResult,
  PostPaths,
  PostRequest,
  PostResult,
  PutPaths,
  PutRequest,
  PutResult,
  RawGetResult,
  RawPatchResult,
  RawPostResult,
  RawPutResult,
} from "./api.types";

type ApiRequestOptions = Pick<AxiosRequestConfig, "responseType"> & {
  /**
   * Overrides the Authorization header for this call. Used by the login-free Ageless Marketing
   * setup link, whose bearer credential is the shared token rather than an admin session.
   */
  bearerToken?: string;
};

// ASP.NET Core binds array query params from repeated `key=value` pairs; axios' default
// bracket style (`key[]=value`) reaches the controller as an empty collection, so the filter
// is silently dropped rather than failing. Endpoints that take array query params must opt in
// here — every path below sends at least one array param.
const REPEATED_KEY_ARRAY_PARAM_PATHS = new Set<string>([
  "/ageless/saas-subscriptions",
  "/ageless/test/onboarding-scenarios/platforms/statuses",
  "/taxonomy/{platformId}/avalara-tax-codes/simulation",
  // Clients list v2: `locationIds` on all four, plus `membershipNames` and `tagSlugs` on the
  // paged query.
  "/platforms/{platformId}/platform-users/paged",
  "/platforms/{platformId}/platform-users/list-summary",
  "/platforms/{platformId}/platform-users/membership-name-facets",
  "/platforms/{platformId}/platform-users/tag-facets",
]);

const usesRepeatedKeyArrayParams = (path: string) =>
  path.includes("/analytics/") || REPEATED_KEY_ARRAY_PARAM_PATHS.has(path);

const getAuthHeaders = (bearerToken?: string) => {
  const token = bearerToken ?? getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Substitutes all path variables from provided args, and return the resulting
 * path and remaining args
 */
// eslint-disable-next-line function-name/starts-with-verb
const derefPath = (p: string, args: Record<string, unknown> = {}) =>
  Object.entries(args).reduce<[string, Record<string, unknown>]>(
    ([path, rest], [k, v]) =>
      path.includes(`{${k}}`)
        ? [path.replace(`{${k}}`, `${v}`), rest]
        : [path, { ...rest, [k]: v }],
    [p, {}],
  );

/**
 * Perform a `GET` request against a known patient app resource
 */
export const get = async <Path extends GetPaths>(
  path: Path,
  args?: GetRequest<Path>,
  options?: ApiRequestOptions,
) => {
  const [resolvedPath, params] = derefPath(`/api/v1${path}`, args ?? {});

  const { data: response, status } = await axios.get<RawGetResult<Path>>(resolvedPath, {
    headers: {
      ...getAuthHeaders(options?.bearerToken),
      "Cache-Control": "no-cache",
    },
    params,
    ...(typeof path === "string" && usesRepeatedKeyArrayParams(path)
      ? { paramsSerializer: { indexes: null } }
      : {}),
    ...options,
  });

  // temporary fix - some endpoints return do not return a { result } object
  if (
    response &&
    SUCCESS_STATUS_CODES.has(status) &&
    typeof response === "object" &&
    response !== null &&
    "result" in response
  ) {
    return response.result as GetResult<Path>;
  } else if (SUCCESS_STATUS_CODES.has(status)) {
    return response as GetResult<Path>;
  } else {
    throw new Error(`Failed to load api get result for ${path}`);
  }
};

/**
 * Perform a `POST` request against a known patient app resource
 */
export const post = async <Path extends PostPaths>(
  path: Path,
  args: PostRequest<Path>,
  options?: ApiRequestOptions,
) => {
  const [resolvedPath, data] = derefPath(`/api/v1${path}`, args ?? {});
  const { queryParams, ...rest } = data ?? {};
  const payload = "$data" in rest ? rest.$data : rest;

  const { data: response, status } = await axios.post<RawPostResult<Path>>(resolvedPath, payload, {
    headers: { ...getAuthHeaders(options?.bearerToken), "Content-Type": "application/json" },
    params: queryParams,
    ...options,
  });

  // temporary fix - some endpoints (private label marketplace) return a string instead of an { result } object
  if (SUCCESS_STATUS_CODES.has(status) && typeof response === "string") {
    return response as PostResult<Path>;
  } else if (
    SUCCESS_STATUS_CODES.has(status) &&
    response &&
    typeof response === "object" &&
    "result" in response
  ) {
    return response.result as PostResult<Path>;
  } else if (SUCCESS_STATUS_CODES.has(status)) {
    // Handle direct object responses (without result wrapper), e.g. 201 Created
    return response as PostResult<Path>;
  } else {
    throw new Error(`Failed to load api post result for ${path}`);
  }
};

/**
 * Perform a `POST` request with FormData (multipart/form-data) against a known patient app resource
 */
export const postFormData = async <Path extends PostPaths>(
  path: Path,
  formData: FormData,
  pathParams?: Record<string, unknown>,
  options?: ApiRequestOptions,
) => {
  const [resolvedPath] = derefPath(`/api/v1${path}`, pathParams ?? {});

  const { data: response, status } = await axios.post<RawPostResult<Path>>(resolvedPath, formData, {
    headers: getAuthHeaders(options?.bearerToken),
    ...options,
  });

  // temporary fix - some endpoints (private label marketplace) return a string instead of an { result } object
  if (SUCCESS_STATUS_CODES.has(status) && typeof response === "string") {
    return response as PostResult<Path>;
  } else if (
    SUCCESS_STATUS_CODES.has(status) &&
    response &&
    typeof response === "object" &&
    "result" in response
  ) {
    return response.result as PostResult<Path>;
  } else if (SUCCESS_STATUS_CODES.has(status)) {
    // Handle direct object responses (without result wrapper) - same as get()
    return response as PostResult<Path>;
  } else {
    throw new Error(`Failed to load api post result for ${path}`);
  }
};

/**
 * Perform a `PUT` request against a known patient app resource
 */
export const put = async <Path extends PutPaths>(
  path: Path,
  args: PutRequest<Path>,
  options?: ApiRequestOptions,
) => {
  const [resolvedPath, rest] = derefPath(`/api/v1${path}`, args ?? {});
  const { queryParams, ...data } = rest;

  const { data: response, status } = await axios.put<RawPutResult<Path>>(
    resolvedPath,
    Array.isArray(data.$data) ? data.$data : data,
    {
      headers: { ...getAuthHeaders(options?.bearerToken), "Content-Type": "application/json" },
      params: queryParams,
      ...options,
    },
  );

  if (SUCCESS_STATUS_CODES.has(status) && typeof response === "string") {
    return response as PutResult<Path>;
  } else if (
    SUCCESS_STATUS_CODES.has(status) &&
    response &&
    typeof response === "object" &&
    "result" in response
  ) {
    return response.result as PutResult<Path>;
  } else if (SUCCESS_STATUS_CODES.has(status)) {
    // Handle direct object/null responses (without result wrapper)
    return response as PutResult<Path>;
  } else {
    throw new Error(`Failed to load api put result for ${path}`);
  }
};

/**
 * Perform a `PATCH` request against a known patient app resource
 */
export const patch = async <Path extends PatchPaths>(
  path: Path,
  args: PatchRequest<Path>,
  options?: ApiRequestOptions,
) => {
  const [resolvedPath, rest] = derefPath(`/api/v1${path}`, args ?? {});
  const { queryParams, ...data } = rest;

  const { data: response, status } = await axios.patch<RawPatchResult<Path>>(
    resolvedPath,
    Array.isArray(data.$data) ? data.$data : data,
    {
      headers: { ...getAuthHeaders(options?.bearerToken), "Content-Type": "application/json" },
      params: queryParams,
      ...options,
    },
  );

  if (SUCCESS_STATUS_CODES.has(status) && typeof response === "string") {
    return response as PatchResult<Path>;
  } else if (
    SUCCESS_STATUS_CODES.has(status) &&
    response &&
    typeof response === "object" &&
    "result" in (response as Record<string, unknown>)
  ) {
    return (response as unknown as { result: PatchResult<Path> }).result;
  } else if (SUCCESS_STATUS_CODES.has(status)) {
    // Handle 204 NoContent and direct payload responses
    return response as PatchResult<Path>;
  } else {
    throw new Error(`Failed to load api patch result for ${path}`);
  }
};

/**
 * Perform a `DELETE` request against a known patient app resource
 */
// eslint-disable-next-line function-name/starts-with-verb
export const del = async <Path extends DeletePaths>(
  path: Path,
  args: DeleteRequest<Path>,
  options?: ApiRequestOptions,
) => {
  const [resolvedPath, data] = derefPath(`/api/v1${path}`, args ?? {});

  const { data: response, status } = await axios.delete(resolvedPath, {
    headers: { ...getAuthHeaders(options?.bearerToken), "Content-Type": "application/json" },
    data: "$data" in data ? data.$data : data,
    ...options,
  });

  if (SUCCESS_STATUS_CODES.has(status)) {
    if (response && typeof response === "object" && "result" in response) {
      return response.result as DeleteResult<Path>;
    }

    // Handle 204 NoContent and direct payload responses
    return response as DeleteResult<Path>;
  } else {
    throw new Error(`Failed to load api delete result for ${path}`);
  }
};

/**
 * The query key the orval client uses for the same request: the resolved URL, then the query
 * params object only when there are any — exactly what `get<OperationId>QueryKey(...)` returns.
 * Both API layers therefore read and invalidate ONE cache entry per request, so a file can move
 * to orval on its own without silently detaching from the invalidations its neighbours issue.
 * `src/api/legacyKeyShape.test.ts` proves the equality against every GET in swagger.json.
 */
export const queryKeyFor = (path: string, args: Record<string, unknown> | undefined) => {
  const [url, params] = derefPath(`/api/v1${path}`, args ?? {});
  return Object.keys(params).length > 0 ? ([url, params] as const) : ([url] as const);
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^$()|[\]\\]/g, "\\$&");

// Path params that were given match their value; the rest match any single path segment.
const toTemplateMatcher = (path: string, known: Record<string, unknown> = {}) =>
  new RegExp(
    `^/api/v1${escapeRegExp(path).replace(/\{([^}]+)\}/g, (_, name: string) =>
      name in known ? escapeRegExp(String(known[name])) : "[^/]+",
    )}$`,
  );

// eslint-disable-next-line function-name/starts-with-verb
export const pget = <P extends GetPaths>(path: P) => {
  type Args = GetRequest<P> extends undefined ? [] : [GetRequest<P>];
  return Object.assign((...[args]: Args) => get(path, args), {
    key: (...[args]: Args) => queryKeyFor(path, args as Record<string, unknown> | undefined),
    keyed: (...[args]: Args) => ({
      queryKey: queryKeyFor(path, args as Record<string, unknown> | undefined),
      queryFn: () => get(path, args),
    }),
    /**
     * Query filters matching this endpoint for any query params and any path params not given —
     * `filters()` is every id, `filters({ platformId })` is every id under that platform. This
     * replaces the old `queryKey: ["/platforms/{platformId}/x"]` prefix match, which the
     * resolved-URL key shape cannot express as a prefix. For one exact request use `key(args)`:
     * as a prefix it already matches every query-param variant of that URL.
     */
    filters: (known: Partial<Record<string, unknown>> = {}) => {
      const matcher = toTemplateMatcher(path, known);
      return {
        predicate: (query: { queryKey: readonly unknown[] }) =>
          typeof query.queryKey[0] === "string" && matcher.test(query.queryKey[0]),
      };
    },
    withOptions: (args: GetRequest<P>, options: ApiRequestOptions) => get(path, args, options),
  });
};

// eslint-disable-next-line function-name/starts-with-verb
export const pput = <P extends PutPaths>(path: P) => {
  return (data: Parameters<typeof put<P>>[1], options?: ApiRequestOptions) =>
    put(path, data, options);
};

// eslint-disable-next-line function-name/starts-with-verb
export const ppost = <P extends PostPaths>(path: P) => {
  return (data: Parameters<typeof post<P>>[1], options?: ApiRequestOptions) =>
    post(path, data, options);
};

// eslint-disable-next-line function-name/starts-with-verb
export const ppostFormData = <P extends PostPaths>(path: P) => {
  return (formData: FormData, pathParams?: Record<string, unknown>, options?: ApiRequestOptions) =>
    postFormData(path, formData, pathParams, options);
};

// eslint-disable-next-line function-name/starts-with-verb
export const pdel = <P extends DeletePaths>(path: P) => {
  return (data: Parameters<typeof del<P>>[1], options?: ApiRequestOptions) =>
    del(path, data, options);
};

// eslint-disable-next-line function-name/starts-with-verb
export const ppatch = <P extends PatchPaths>(path: P) => {
  return (data: Parameters<typeof patch<P>>[1], options?: ApiRequestOptions) =>
    patch(path, data, options);
};
